import {
  type LinearSubstep,
  SimpleCsticId,
  type SubstepTile,
  type SubstepTileExtended,
} from "~/Configurators/Linear/models";
import SubstepFrame from "~/Configurators/Linear/components/SubstepFrame";
import { ConfiguratorType, type ShaftConfiguratorType } from "~/Configurators/ConfiguratorType";
import { useConfiguratorType } from "~/Configurators/common/useConfiguratorType";
import { isDebugLanguageEnabled } from "~/utils/language/isDebugLanguageEnabled";
import { TileContainer } from "~/Configurators/common/components/GenericTileSubstep/GenericTileSubstep";
import LinearTile, {
  LCTileImage,
  type LinearTileProps,
  StandardStarComponent,
} from "~/Configurators/Linear/components/LinearTile";
import { useDispatch, useSelector } from "react-redux";
import { formatCsticsToSet } from "~/Configurators/Linear/utils";
import { type CustomDispatch } from "~/redux/types";
import { type RootState } from "~/redux/store";
import { getKmats } from "~/Configurators/Linear/redux/selectors";
import { isTileEnabled, isTileStandard } from "~/Configurators/common/tileUtils";
import { updateCsticValuesFromTile } from "~/Configurators/Linear/redux/actions";
import classes from "./ShaftToleranceSubstep.module.less";
import LanguageAwareExternalContent from "~/Configurators/Linear/components/LanguageAwareExternalContent";
import { Card } from "~/Configurators/Linear/components/Card";
import LinearInfo from "~/Configurators/Linear/components/LinearInfo";
import classNames from "classnames";
import { SubstepType } from "~/Configurators/Linear/dtos";
import { selectCstic } from "~/Configurators/Linear/redux/selectCstic";
import { useXmlConfiguratorTranslations } from "~/Configurators/common/useXmlConfiguratorTranslations";
import * as MetricCstics from "~/Configurators/Shaft/metric/metricCsticConstants";
import * as InchCstics from "~/Configurators/Shaft/inch/inchCsticConstants";
import { CheckedCircle } from "~/common/components/CheckedCircle/CheckedCircle";
import { SimpleCsticNumberField } from "~/Configurators/Shaft/components/SimpleCsticNumberField";
import { formatShaftToleranceMinmax } from "~/Configurators/Shaft/shaftConfiguratorHelper";
import { INCH_UNIT, METRIC_UNIT } from "~/Configurators/Shaft/common/commonShaftConstants";
import { KmatType } from "~/Configurators/Linear/kmatType";

interface Props {
  // Actually substepIndex
  csticIndex: number;
  // Actually substepAmount
  csticAmount: number;
  def: LinearSubstep;
}

const CONFIGURATION = {
  [ConfiguratorType.MetricShaft]: {
    kmatType: KmatType.Main,
    cstic: SimpleCsticId(MetricCstics.shaftTolerance.value),
    values: ["LN", "LS", "LNS"],
    minmax: {
      ln: SimpleCsticId(MetricCstics.shaftTolerance.minmax.ln),
      ls: SimpleCsticId(MetricCstics.shaftTolerance.minmax.ls),
    },
    maxDecimal: MetricCstics.shaftTolerance.maxDecimal,
    unit: METRIC_UNIT,
    specialTile: {
      value: "LNS",
      minmax: "VLMAX_01T_060",
      upper: "VLMAX_01_02_060",
      lower: "VLMAX_01_01_060",
    },
  },
  [ConfiguratorType.InchShaft]: {
    kmatType: KmatType.Main,
    cstic: SimpleCsticId(InchCstics.shaftTolerance.value),
    values: ["LN", "LS"],
    minmax: {
      ln: SimpleCsticId(InchCstics.shaftTolerance.minmax.ln),
      ls: SimpleCsticId(InchCstics.shaftTolerance.minmax.ls),
    },
    maxDecimal: InchCstics.shaftTolerance.maxDecimal,
    unit: INCH_UNIT,
  },
};

const generateTiles = (
  def: LinearSubstep,
  dispatch: CustomDispatch<RootState>,
  config: (typeof CONFIGURATION)[keyof typeof CONFIGURATION],
): (SubstepTileExtended & { value: string })[] => {
  const tiles: (SubstepTile & { value: string })[] = config.values.map((value, index) => ({
    cstics: { [config.cstic.key]: value },
    value,
    name: `configurator.shaft.step.2.substep.2.tile.${index + 1}.title`,
    description: "",
    isDisabledExternally: false,
    info: "",
    imageUrl: "",
    csticsToEnable: {},
  }));
  return tiles.map((tile, index) => ({
    ...tile,
    onClick: () => dispatch(updateCsticValuesFromTile(def.type, 0, tile)),
    tileIndex: index,
  }));
};

export const ShaftToleranceSubstep = ({ def, ...props }: Props) => {
  const configuratorType = useConfiguratorType() as ShaftConfiguratorType;
  const config = CONFIGURATION[configuratorType];
  const debugLanguage = isDebugLanguageEnabled();

  const dispatch: CustomDispatch<RootState> = useDispatch();
  const kmats = useSelector(getKmats);

  const tiles = generateTiles(def, dispatch, config);

  const curValue = useSelector(selectCstic(config.cstic))?.value;

  let minmaxLN = useSelector(selectCstic(config.minmax.ln))?.value;
  minmaxLN = formatShaftToleranceMinmax(minmaxLN, config.unit, config.maxDecimal);
  let minmaxLS = useSelector(selectCstic(config.minmax.ls))?.value;
  minmaxLS = formatShaftToleranceMinmax(minmaxLS, config.unit, config.maxDecimal);

  const minmaxes = {
    LN: minmaxLN,
    LS: minmaxLS,
  };

  return (
    <SubstepFrame
      title={def.name}
      description=""
      info={def.info}
      isPicked={def.selectedTile !== null}
      errorMessage={""}
      {...props}
    >
      <TileContainer columnAmount={4} className={classes.tileContainer}>
        {tiles.slice(0, 2).map((data, index) => (
          <LinearTile
            key={index}
            onClickHandler={data.onClick}
            imageUrl={data.imageUrl}
            title={debugLanguage ? formatCsticsToSet(data.cstics) : data.name}
            description={minmaxes[data.value as keyof typeof minmaxes]}
            info={data.info}
            isPicked={curValue === config.values[index]}
            isDisabled={!isTileEnabled(data, kmats)}
            isStandard={isTileStandard(data, kmats)}
          />
        ))}
        {/* Disabled until further notice */}
        {/*{tiles[2] && (*/}
        {/*  <WideTile*/}
        {/*    index={2}*/}
        {/*    className={classes.wideTile}*/}
        {/*    onClickHandler={tiles[2].onClick}*/}
        {/*    imageUrl={tiles[2].imageUrl}*/}
        {/*    title={debugLanguage ? formatCsticsToSet(tiles[2].cstics) : tiles[2].name}*/}
        {/*    description={tiles[2].description}*/}
        {/*    info={tiles[2].info}*/}
        {/*    isPicked={tiles[2].tileIndex === def.selectedTile}*/}
        {/*    isDisabled={!isTileEnabled(tiles[2], kmats)}*/}
        {/*    isStandard={isTileStandard(tiles[2], kmats)}*/}
        {/*  />*/}
        {/*)}*/}
      </TileContainer>
    </SubstepFrame>
  );
};

/*
Copy from Linear Tile - Make Linear Tile a composable component...
 */
const WideTile = (props: LinearTileProps) => {
  const {
    className,
    description,
    title,
    isDisabled,
    isPicked,
    imageUrl,
    info,
    onClickHandler,
    link,
    openLinkInNewTab,
    isStandard,
    forceStandardSpace,
    children,
    selectable = true,
  } = props;

  const { t } = useXmlConfiguratorTranslations();

  const titleElement =
    typeof title === "string" ? (
      <LanguageAwareExternalContent content={title} loadedClassname={"scha__lc__external-label"} />
    ) : (
      title
    );

  const config = CONFIGURATION[ConfiguratorType.MetricShaft];

  const minmax = useSelector(selectCstic({ key: config.specialTile.minmax, index: 0, type: config.kmatType }))?.value;

  return (
    <Card
      className={classNames("scha__lc__tile", className)}
      onClick={onClickHandler}
      disabled={isDisabled}
      active={isPicked}
      disableHover={isDisabled}
      as={link ? "a" : "div"}
      href={link}
      target={link && openLinkInNewTab ? "_blank" : undefined}
    >
      {imageUrl && <LCTileImage imageUrl={imageUrl} />}

      {selectable && <CheckedCircle className={"scha__lc__tile__checkmark"} checked={isPicked} />}

      <div className={classNames("scha__lc__card__contents", classes.wideTileContent)}>
        <div>
          <p className="scha__lc__card__headline">
            {titleElement}
            <LinearInfo info={info} popupClassName={"scha__lc__external-content--shaft"} />
          </p>

          {description && (
            <p className="scha__lc__card__subheadline">
              <LanguageAwareExternalContent
                content={minmax ? `⁺∕₋ ${minmax}` : undefined}
                loadedClassname={"scha__lc__external-label"}
              />
            </p>
          )}

          {(isStandard || forceStandardSpace) && (
            <div className={!isStandard ? "v-hidden" : "mt-auto"}>
              <StandardStarComponent />
            </div>
          )}

          {children}
        </div>

        <div onClick={isPicked ? STOP_PROPAGATION : undefined}>
          <SimpleCsticNumberField
            csticId={SimpleCsticId(config.specialTile.upper)}
            substepType={SubstepType.ShaftTolerance}
            label={t!("configurator.shaft.step.2.substep.2.tile.3.lower")}
            disabled={!isPicked}
          />
          <SimpleCsticNumberField
            csticId={SimpleCsticId(config.specialTile.lower)}
            substepType={SubstepType.ShaftTolerance}
            label={t!("configurator.shaft.step.2.substep.2.tile.3.lower")}
            disabled={!isPicked}
          />
        </div>
      </div>
    </Card>
  );
};

const STOP_PROPAGATION = (e: React.MouseEvent) => {
  e.stopPropagation();
};
